import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCookies } from "react-cookie";
import LocationAutocomplete from "../CustomMui/LocationStreetAutocomplete";

/* helper to show YYYY‑MM‑DD → MM/DD/YYYY */
const fmt = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};

const Settings = () => {
  const navigate = useNavigate();
  const [cookies, , removeCookie] = useCookies(["auth_token"]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");

  const [company, setCompany] = useState({
    company_name: "", username: "", company_address: "", billing_address: "",
    description: "", membership_level: "", status: "",
    membership_start_date: "", membership_end_date: ""
  });
  const [original, setOriginal] = useState(null);
  const [changed, setChanged] = useState(false);

  /* ───────────────── fetch on mount ───────────────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/api/company/settings", {
          headers: { Authorization: `Bearer ${cookies.auth_token}` }
        });
        if (data.success) {
          setCompany(data.company);
          setOriginal(data.company);
        } else setError("Failed to load company data");
      } catch {
        setError("Error loading company data");
      } finally {
        setLoading(false);
      }
    })();
  }, [cookies.auth_token]);

  /* form diff detection */
  useEffect(() => {
    if (original) setChanged(JSON.stringify(company) !== JSON.stringify(original));
  }, [company, original]);

  /* handle simple inputs */
  const handleInput = ({ target: { name, value } }) =>
    setCompany((prev) => ({ ...prev, [name]: value }));

  const logout = () => {
    removeCookie("auth_token", { path: "/" });
    navigate("/login");
  };

  /* ───────────────── save edits ───────────────── */
  const saveChanges = async (e) => {
    e.preventDefault();

    /* client‑side checks (condensed) */
    if (company.company_name.length > 30)     return setError("Company name ≤ 30 chars");
    if (company.username.length < 8 || company.username.length > 30)
      return setError("Username 8–30 chars");
    if (!company.company_address || !company.billing_address)
      return setError("Addresses are required");

    try {
      setSaving(true); setError(null);

      /* send only editable fields */
      const payload = {
        company_name:   company.company_name,
        username:       company.username,
        company_address:company.company_address,
        billing_address:company.billing_address,
        description:    company.description
      };

      const { data } = await axios.put("/api/company/settings", payload, {
        headers: { Authorization: `Bearer ${cookies.auth_token}` }
      });

      if (!data.success) return setError(data.message || "Update failed");

      /* refresh state from server */
      setCompany(data.company);
      setOriginal(data.company);
      setChanged(false);
      setSuccess("Company information updated!");

      /* if username changed, log out */
      if (original && original.username !== data.company.username) {
        setSuccess("Username changed now logging you out…");
        setTimeout(() => {
          logout();
          window.location.reload();
        }, 2000);
      }

      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Error updating company information");
    } finally {
      setSaving(false);
    }
  };

  /* ───────────────── renew ───────────────── */
  const renew = async () => {
    if (company.status === "Renew") return;
    if (!window.confirm("Renew Partnership?")) return;

    try {
      setSaving(true); setError(null);
      const { data } = await axios.put(
        "/api/company/settings",
        { status: "Renew" },
        { headers: { Authorization: `Bearer ${cookies.auth_token}` } }
      );
      if (!data.success) return setError(data.message || "Renew failed");

      setCompany(data.company);
      setOriginal(data.company);
      setChanged(false);
      setSuccess("Partnership renewed!");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Error Renewing Partnership");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    if(original) {
      setCompany(original);
      //Need to manually reset the location fields
      document.getElementById("settings-company-address").getElementsByTagName("input")[0].value=original.company_address;
      document.getElementById("settings-billing-address").getElementsByTagName("input")[0].value=original.billing_address;
      setChanged(false);
    } //original && (setCompany(original), setChanged(false))
  };
  const dismiss = () => (setError(null), setSuccess(""));

  /* ───────────────── render ───────────────── */
  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading…</div>;

  return (
    <div className="Homepage flex flex-col justify-center h-full">
      <div className="login-container">
        <div className="Homepage-page flex flex-col w-full max-w-4xl py-4 mx-auto my-auto">
          {/* Box 1: Title */}
          <div className="bg-white rounded-2xl shadow p-6 text-black mb-4">
            <h2 className="text-3xl normal-case no-underline">
              Company Settings
            </h2>
          </div>
          <div className="bg-white rounded-2xl shadow-md p-6 text-black mb-4">
        <form onSubmit={saveChanges} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <label className="block mb-1">Company Name *</label>
              <input name="company_name" className="w-full px-3 py-2 border rounded-md"
                value={company.company_name} onChange={handleInput} maxLength={30} required />
            </div>

            <div>
              <label className="block mb-1">Username *</label>
              <input name="username" className="w-full px-3 py-2 border rounded-md"
                value={company.username} onChange={handleInput} maxLength={30} required />
            </div>

            <div>
              <label className="block mb-1">Company Address *</label>
              <LocationAutocomplete idAndClassName="settings-company-address"
                locationValue={company.company_address}
                setLocationValue={(val) => setCompany((p) => ({ ...p, company_address: val }))} />
            </div>

            <div>
              <label className="block mb-1">Billing Address *</label>
              <LocationAutocomplete idAndClassName="settings-billing-address"
                locationValue={company.billing_address}
                setLocationValue={(val) => setCompany((p) => ({ ...p, billing_address: val }))} />
            </div>

            <div className="md:col-span-2">
              <label className="block mb-1">Description</label>
              <textarea name="description" rows={4} maxLength={500}
                className="w-full px-3 py-2 border rounded-md"
                value={company.description} onChange={handleInput} />
            </div>

            <div>
              <label className="block mb-1">Partnership Level</label>
              <input readOnly className="w-full px-3 py-2 border rounded-md bg-gray-100"
                value={company.membership_level} />
            </div>

            <div>
              <label className="block mb-1">Start Date</label>
              <input readOnly className="w-full px-3 py-2 border rounded-md bg-gray-100"
                value={fmt(company.membership_start_date)} />
            </div>

            <div>
              <label className="block mb-1">End Date</label>
              <input readOnly className="w-full px-3 py-2 border rounded-md bg-gray-100"
                value={fmt(company.membership_end_date)} />
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-6">
            <button type="submit" disabled={!changed || saving}
              className="px-4 py-2 border rounded-md">
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button type="button" onClick={resetForm}
              className="px-4 py-2 border rounded-md">Reset</button>
            <button type="button" onClick={renew}
              disabled={saving || company.status === "Pending" || company.status === "New" || company.status === "Renew" || company.status === "BilledNew" || company.status === "BilledRenew"}
              className="px-4 py-2 border rounded-md">
              {company.status === "Renew" || company.status === "BilledRenew" ? "Already Renewed"
                : saving ? "Renewing…" : "Renew Partnership"}
            </button>
            <button type="button" onClick={() => navigate(-1)}
              className="px-4 py-2 border rounded-md">Cancel</button>
          </div>
        </form>
      </div>

      {(error || success) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full text-center">
            {error && <p className="text-red-600 mb-2">{error}</p>}
            {success && <p className="text-green-600 mb-2">{success}</p>}
            <button onClick={dismiss} className="px-4 py-2 border rounded-md">Close</button>
          </div>
        </div>
      )}
      </div>
    </div>
    </div>
  );
};

export default Settings;
